import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {toast, ToastContainer} from 'react-toastify';import { handleDeleteDataset } from "../../modules/datasetModule.ts";
import { uploadMultipleFilesWithProgress } from "../../components/common/FileUploadProgress.tsx";
import { handleAddFiles } from "../../modules/fileListModules.ts";
import { handleFetchTable, handleFetchTablePreview, handleFetchTemplate } from "../../modules/templateModules.ts";
import ReusableTable from "../../components/common/Table.tsx";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { CardBody, Col, Input, Progress, Row } from "reactstrap";
import { LabelText } from "../../components/common/Labeltext.tsx";
import SelectField from "../../components/common/SelectField.tsx";
import { FileUploadOutlined } from "@mui/icons-material";

interface FileResponse {
  dataset_name: string;
  file_name: string;
  total_fields: number;
  total_records: number;
  created_by: number;
  id: number;
  created_at: string;
  modified_at: string;
}

// Updated interface to match the new API response structure
interface UploadResponse {
  files: {
    dataset_id: number;
    id: number;
    file_name: string;
    created_at: string;
    modified_at: string;
    total_fields: number;
    total_records: number;
    error_report: string;
  }[];
}

interface Template {
  id: number;
  template_name: string;
  total_fields: number;
  created_at: string;
}

interface FileUploadState {
  file: File;
  id: string; // unique identifier for each file
}

const DataSet: React.FC = () => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [datasetName, setDatasetName] = useState<string>("");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<{
    label: string;
    id: number;
  } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(50);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadedData, setUploadedData] = useState<UploadResponse | null>(null);
  const [datasetId, setDatasetId] = useState<number | null>(null);
  const [isFileUploadMode, setIsFileUploadMode] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const templateName = location.state?.templateName || "";
  const datasetNames = location.state?.datasetName || "";
  const [usage, setUsage] = useState<string>(location.state?.usage || "t");
  const [tableLoading, setTableLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [previewTableLoading, setPreviewTableLoading] = useState(false);
  const [previewTableError, setPreviewTableError] = useState(null);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  //const toast = useToast();
  const navigate = useNavigate();
  const acceptedTypes = ["text/csv"];
  const isValidFileType = (file: File): boolean =>
    acceptedTypes.includes(file.type);

  const datasource = location?.state?.datasource
  const isDatalake = (datasource && datasource === "datalake")
  const generateFileId = (): string => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  const getButtonLabel = () => {
    if (isFileUploadMode) {
      return `Add Files to Dataset (${files.length} files)`;
    }

    if (uploadedData) {
      return `Navigate to Files (${files.length} files)`;
    }

    return `Confirm ${
      datasource !== "datalake" ? `(${files.length} files)` : ""
    }`;
  };

  const handleOpenFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-CA");
  };

  // Check if URL contains fileUpload and extract dataset_id
  useEffect(() => {
    if (location.pathname.includes('fileUpload')) {
      setIsFileUploadMode(true);
      // Extract dataset_id from URL (assuming format: /fileUpload/5)
      const pathParts = location.pathname.split('/');
      const fileUploadIndex = pathParts.findIndex(part => part === 'fileUpload');
      if (fileUploadIndex !== -1 && pathParts[fileUploadIndex + 1]) {
        const extractedDatasetId = parseInt(pathParts[fileUploadIndex + 1]);
        if (!isNaN(extractedDatasetId)) {
          setDatasetId(extractedDatasetId);
        }
      }
    } else {
      setIsFileUploadMode(false);
      setDatasetId(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (datasetNames) {
      setDatasetName(datasetNames);
    }
  }, [datasetNames]);

  const extractTemplates = (data: any): Template[] => {
    if (Array.isArray(data)) return data;

    if (
      data &&
      typeof data === "object" &&
      "data" in data &&
      "total" in data
    ) {
      return data.data;
    }

    return [];
  };

  const fetchDatalakeTemplates = async () => {
    setTableLoading(true);

    const data = await handleFetchTable(0, 1000, 1);
    const templatesData = extractTemplates(data);

    const formatted = templatesData.map((template: Template) => ({
      template_name: template,
    }));

    setTemplates(formatted);
  };

  const fetchRegularTemplates = async () => {
    setTemplateLoading(true);

    const data = await handleFetchTemplate(0, 1000, 1);
    const templatesData = extractTemplates(data);

    const formatted = templatesData.map((template: Template) => ({
      ...template,
      created_at: formatDate(template.created_at),
    }));

    setTemplates(formatted);

    if (templateName) {
      const found = formatted.find(
        (t) => t.template_name === templateName
      );

      if (found) {
        setSelectedTemplate({
          label: found.template_name,
          id: found.id,
        });
      }
    }
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        if (isDatalake) {
          await fetchDatalakeTemplates();
        } else {
          await fetchRegularTemplates();
        }
      } catch (error) {
        console.error(error);
      } finally {
        setTableLoading(false);
        setTemplateLoading(false);
      }
    };

    fetchTemplates();
  }, [templateName]);

  const changeTemplate = (selectedTable) => {
    setSelectedTemplate(selectedTable);
    const fetchTablePreview = async () => {
      try {
        if (datasource && isDatalake) {
          setPreviewTableLoading(true)
          const data = await handleFetchTablePreview(selectedTable);
          const col = Object.keys(data.preview[0]).map((column) => ({
            accessorKey: column,
            header: column
          }))
          setPreviewColumns(col);
          setPreviewData(data.preview)
          setPreviewTableLoading(false)
        }
      } catch (error) {
        console.error(error);
        setPreviewTableError(error);
        toast.error(error);
      } finally {
        setPreviewTableLoading(false);
      }
    };
    fetchTablePreview();
  }

  const addFile = (newFile: File) => {
    // Check if file with same name already exists
    const existingFile = files.find(f => f.file.name === newFile.name);
    if (existingFile) {
      toast.error(`File "${newFile.name}" is already added`);
      return;
    }

    const fileState: FileUploadState = {
      file: newFile,
      id: generateFileId(),
    };

    setFiles(prevFiles => [...prevFiles, fileState]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    selectedFiles.forEach(selectedFile => {
      if (isValidFileType(selectedFile)) {
        addFile(selectedFile);
      } else {
        toast.error(`"${selectedFile.name}" is not a valid CSV file`);
      }
    });

    // Reset the input value to allow selecting the same file again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    droppedFiles.forEach(droppedFile => {
      if (isValidFileType(droppedFile)) {
        addFile(droppedFile);
      } else {
        toast.error(`"${droppedFile.name}" is not a valid CSV file`);
      }
    });
  };

  const handleRemoveFile = async (fileId: string) => {
    // If files are already uploaded, we need to delete them from the server
    if (uploadedData && uploadedData.files.length > 0) {
      try {
        // Delete all uploaded datasets since we can't delete individual files from a batch
        for (const file of uploadedData.files) {
          await handleDeleteDataset(file.dataset_id);
        }
        setUploadedData(null);
        toast.success("Uploaded datasets deleted successfully!");
      } catch (error) {
        console.error("Error deleting datasets:", error);
        toast.error("Failed to delete uploaded datasets.");
      }
    }

    setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
  };


  const validateSubmit = () => {
    if (!datasetName.trim()) {
      toast.error("Please enter a dataset name");
      return false;
    }

    if (!selectedTemplate) {
      toast.error(
        `Please select a ${
          isDatalake ? "Table" : "Template"
        }`
      );
      return false;
    }

    if (files.length === 0 && datasource !== "datalake") {
      toast.error("Please upload at least one CSV file");
      return false;
    }

    return true;
  };

const startUpload = () => {
  setIsUploading(true);
  setUploadProgress(0);
};

const stopUpload = () => {
  setIsUploading(false);
};

const extractFiles = () => files.map(f => f.file);

const navigateToFiles = (id: number, templateLabel: string) => {
  navigate(`/files/${id}`, {
    state: {
      datasetName: datasetName.trim(),
      templateName: templateLabel,
      usage,
    },
  });
};

const handleUploadError = (error: any, action: string) => {
  setUploadProgress(0);

  if (error?.response?.status === 422) {
    const detail = error.response.data.detail;
    setErrorMessage(detail.message || `Error during ${action}`);
    setErrorModalOpen(true);
    return;
  }

  const msg =
    error?.response?.data?.detail ||
    `Failed to ${action}. Please try again.`;

  toast.error(`Error ${action}: ${msg}`);
};

const processFileUploadMode = async () => {
  startUpload();
  try {
    await handleAddFiles(datasetId!, extractFiles());

    setUploadProgress(100);
    toast.success(`${files.length} file(s) added successfully!`);

    navigateToFiles(datasetId!, templateName.trim());
  } catch (err) {
    handleUploadError(err, "adding files");
  } finally {
    stopUpload();
  }
};

const processExistingUpload = () => {
  const id = uploadedData.files[0].dataset_id;

  toast.success(
    `${uploadedData.files.length} dataset(s) submitted successfully!`
  );

  navigateToFiles(id, selectedTemplate.label);
};

const processNewUpload = async () => {
  startUpload();

  try {
    const response = await uploadMultipleFilesWithProgress(
      isDatalake ? undefined : extractFiles(),
      datasetName,
      isDatalake ? undefined : selectedTemplate.id,
      (p) => setUploadProgress(p),
      usage,
      datasource,
      selectedTemplate.label
    );

    setUploadedData(response);

    const id =
      response.files?.[0]?.dataset_id || response.dataset_id;

    toast.success(
      !response.file
        ? "Dataset created successfully"
        : `${response.files.length} dataset(s) submitted successfully!`
    );

    if (isDatalake) {
      navigate("/Datalist");
    } else {
      navigateToFiles(id, selectedTemplate.label);
    }
  } catch (err) {
    handleUploadError(err, "uploading files");
  } finally {
    stopUpload();
  }
};

const handleSubmit = async () => {
  if (!validateSubmit()) return;

  if (isFileUploadMode && datasetId) {
    await processFileUploadMode();
    return;
  }

  if (uploadedData?.files?.length > 0) {
    processExistingUpload();
    return;
  }

  await processNewUpload();
};

  const handleErrorModalClose = () => {
    setErrorModalOpen(false);
    navigate("/Datalist");
  };

  const handleCancel = async () => {
    // Delete uploaded datasets if any (only for regular flow)
    if (!isFileUploadMode && uploadedData && uploadedData.files.length > 0) {
      for (const file of uploadedData.files) {
        try {
          await handleDeleteDataset(file.dataset_id);
        } catch (error) {
          console.error("Error deleting dataset:", error);
        }
      }
    }

    setFiles([]);
    setDatasetName("");
    setSelectedTemplate(null);
    setUploadedData(null);
    setUploadProgress(0);
    setIsUploading(false);
    navigate("/Datalist");
  };

  return (
    <>
      <BreadCrumb moduleName={"Dataset"} pageName={isFileUploadMode ? "Upload file" : "Create Dataset"} viewName={isFileUploadMode ? datasetName : null} />
      <ToastContainer position="top-center" />
      <div className="listingjs-table " id="customerList">
        <div className="live-preview">
          <div className="align-items-center d-flex mb-3 ">
            <h4 className="card-title mb-0 flex-grow-1">{isFileUploadMode ? `Add files to dataset` : "Import Files"}</h4>
          </div>
          <div className="border table-border p-3">
            <div className=''>
              <CardBody>
                <Row className="gy-2 pt-1 pb-2 ">
                  <Col lg={6}>
                    <LabelText
                      htmlFor={"DatasetName"}
                      className={"text-danger"}
                      displayText={"Dataset Name"}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"DatasetPlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={`${!isFileUploadMode ? "Enter your dataset name" : "Dataset name is read-only when adding files to existing dataset"}`}
                    />
                    <Input
                      type="text"
                      value={datasetName}
                      disabled={isUploading || isFileUploadMode}
                      onChange={(e) => setDatasetName(e.target.value)}
                    />
                  </Col>
                  <Col lg={6}>
                    <LabelText
                      htmlFor={"DatasetTemplate"}
                      className={"text-danger"}
                      displayText={`Choose ${datasource && isDatalake ? "Table" : "Template"}`}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"DatasetPlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={`Select a ${datasource && isDatalake ? "Table" : "Template"}`}
                    />
                    <SelectField
                      className={""}
                      value={selectedTemplate}
                      placeholder={" "}
                      isDisabled={isUploading || isFileUploadMode}
                      handleChange={(selectedOption) => {
                        changeTemplate(selectedOption)
                      }}
                      showLoadingIndicator={(isDatalake && tableLoading) || (datasource !== "datalake" && templateLoading)}
                      options={templates.map((template) =>
                        isDatalake
                          ? { label: template.template_name, value: template.template_name }
                          : { label: template.template_name, id: template.id, value: template.template_name }
                      )}
                      isRVStyle={true}
                    />
                  </Col>
                  <Col lg={6} className="mt-4">
                    <div>
                      <LabelText
                        htmlFor={"DatasetUsage"}
                        className={"text-danger"}
                        displayText={`Select Dataset Usage`}
                        important={"*"}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '200px' }}>
                      <div>
                        <Input
                          type="radio"
                          name="DatasetUsage"
                          className="me-2"
                          disabled={isFileUploadMode || isUploading}
                          checked={usage === "t"}
                          onChange={() => {
                            if (!isFileUploadMode && !isUploading)
                              setUsage("t")
                          }}
                        />
                        <LabelText displayText={"Training"} classLabelName={"rv-text-color"} />
                      </div>
                      <div>
                        <Input
                          type="radio"
                          name="DatasetUsage"
                          className="me-2"
                          disabled={isFileUploadMode || isUploading}
                          checked={usage === "p"}
                          onChange={() => {
                            if (!isFileUploadMode && !isUploading)
                              setUsage("p")
                          }}
                        />
                        <LabelText displayText={"Prediction"} classLabelName={"rv-text-color"} />
                      </div>
                    </div>
                  </Col>
                  {(datasource !== "datalake") &&
                    <Col lg={12} className="mt-4">
                      <div className="image-div-tag mb-0 w-100">
                        <LabelText
                          htmlFor={"file"}
                          classLabelName={"ms-0 rv-form-label"}
                          displayText={"Browse Files"}
                        />
                        <LabelText
                          htmlFor={"file"}
                          classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                          displayText={"Drag and drop a file here, or browse to upload (CSV)."}
                        />
                        <div
                          className={`image-border-style ms-0`}
                        >
                          <label htmlFor="fileInput" className="cursor-img">
                            <FileUploadOutlined
                              className="fs-1 align-middle me-1 text-secondary"
                              style={{ transform: "rotate(0deg)" }}
                            />
                          </label>

                          <input
                            onDragOver={(e) => {
                              e.preventDefault();
                              if (!isUploading) setIsDragging(true);
                            }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                            id="fileInput"
                            type="file"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            accept=".csv,text/csv"
                            multiple
                            onChange={handleFileSelect}
                            disabled={isUploading}
                          />
                        </div>
                      </div>
                    </Col>}
                  {files.length > 0 && (
                    <Col log={12}>
                      <LabelText
                        htmlFor={"file"}
                        classLabelName={"ms-0 mt-2 rv-form-label"}
                        displayText={`Selected Files (${files.length})`}
                      />
                      {files?.map((fileState) => (
                        <div className="border table-border p-3 mb-1">
                          <div className=''>
                            <CardBody>
                              <div className="d-flex  justify-content-between">
                                <div className="d-flex">
                                  <div className="mb-1">
                                    <i className="ri-file-3-fill ri-2x text-primary" />
                                  </div>
                                  <div className="ms-2">
                                    <p className="mb-0">{fileState.file.name}</p>
                                    <div className="d-flex">
                                      <p className="rv-sub-label rv-label-muted mt-0">{(fileState.file.size / 1024).toFixed(2)} KB</p>
                                      {uploadedData && <p className="rv-sub-label rv-label-muted mt-0 ms-4"> • Uploaded</p>}
                                      {isUploading && <p className="rv-sub-label rv-label-muted mt-0 ms-4"> • Processing...</p>}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-3">
                                  <i className="ri-delete-bin-line ri-xl text-danger" onClick={() => handleRemoveFile(fileState.id)} />
                                </div>
                              </div>
                            </CardBody>
                          </div>
                        </div>
                      ))}
                    </Col>)}
                  {isUploading && (
                    <Row>
                      <Col lg={12}>
                        <div className="border table-border w-100 p-3 mb-1">
                          <div className=''>
                            <CardBody>
                              <div>
                                {isDatalake ? "Creating dataset..." : isFileUploadMode ? `Adding ${files.length} file(s) to dataset...` : `Uploading ${files.length} file(s)...`}
                                <Progress max={100} value={uploadProgress} className="mt-2 w-100"></Progress>
                                <span className="d-flex justify-content-end mt-1">{uploadProgress.toFixed(0)}%</span>
                              </div>
                            </CardBody>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Row>
              </CardBody>
            </div>
          </div>

          <Row lg={6} className=" justify-content-end  mt-4">
            <div className="live-preview">
              <div className="d-flex justify-content-end gap-2 mb-5">
                <button
                  type="button"
                  className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
                  onClick={handleCancel}
                  disabled={isUploading}
                >
                  <i className="ri-close-line label-icon secondary-label-icon align-middle fs-16 me-2"></i>
                  <span className="px-2 ms-2 me-1 fw-bold">
                    Cancel
                  </span>
                </button>
                <button
                  onClick={handleSubmit}
                  type="submit"
                  disabled={isUploading}
                  className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
                >
                  <i className="ri-save-line label-icon align-middle fs-16 me-1"></i>
                  <span className="px-2 ps-3 ms-2 me-1 fw-bold">
                    {getButtonLabel()}
                  </span>
                </button>
              </div>
            </div>
          </Row>
          {isDatalake && (
            <div className="live-preview">
              <div className="container-fluid px-0">
                <div className="align-items-center d-flex mb-3 ">
                  <h4 className="card-title mb-0 flex-grow-1">Table Preview</h4>
                </div>
                <div className="disable-margin-card table-responsive table-customer border table-border w-100">
                  <ReusableTable
                    columns={previewColumns}
                    data={previewData}
                    loading={previewTableLoading}
                    error={previewTableError}
                    enablePagination={false}
                    enableSorting={false}
                    enableColumnFilters={false}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DataSet;